const PERMISSIONS_ENDPOINT = '/gateway/api/permissions/permitted';
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 500;

type PermissionsResponse = {
	permitted: boolean;
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Checks whether the current user has Loom write permission for the given cloud site.
 *
 * Calls `POST /gateway/api/permissions/permitted` with a Loom site ARI built from
 * the supplied `cloudId`. Includes retry logic (2 attempts, 500 ms apart).
 *
 * @param cloudId - The Atlassian cloud site ID to check Loom permission for.
 * @returns `true` when the user is permitted, `false` otherwise (including on error).
 */
export const checkLoomPermission = async (cloudId: string): Promise<boolean> => {
	const body = JSON.stringify({
		resourceId: `ari:cloud:loom::site/${cloudId}`,
		permissionId: 'write',
	});

	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
		try {
			const response = await fetch(PERMISSIONS_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body,
			});

			if (!response.ok) {
				throw new Error(`API responded with HTTP ${response.status}`);
			}

			const data: PermissionsResponse = await response.json();
			return data.permitted;
		} catch (error) {
			if (attempt < MAX_ATTEMPTS) {
				await delay(RETRY_DELAY_MS);
			}
		}
	}

	return false;
};
